import React, { useEffect, useRef, useState } from 'react';
import { Container, Button, Modal } from 'react-bootstrap';
import AsyncSelect from 'react-select/async';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { FaMapMarkerAlt, FaChartLine, FaRegClock } from 'react-icons/fa';
import { debounce } from 'lodash';
import axios from 'axios';
import ComparableProperties from '../components/ComparableProperties';
import EnquiryModal from '../components/EnquiryModal';
import { useAgentDetails } from '../utils/useAgentDetails';
import { setMetaTags, trackEvent, handleTokenAndPostRequest } from '../utils/utils';
import './PropertySearchLive.css';
import './MarketActivity.css';
import './Styles.css';

const MOBILE_BREAKPOINT = 768;

const WELCOME_STEPS = [
  { icon: '1', text: 'Search any suburb to pull up recent sold and rented properties.' },
  { icon: '2', text: 'Filter by sold or rent, time period, property type and bedrooms.' },
  { icon: '3', text: 'Exclude properties unlike yours to build an accurate price range.' },
];

/**
 * Standalone Market Activity page. Lets the visitor search for a suburb, then
 * renders the shared ComparableProperties ("Market Activity") component for the
 * chosen suburb. Styled to match the /property-search-live landing page.
 */
const MarketActivity = () => {
  const { agentDetails, branding } = useAgentDetails();
  const [selectedSuburb, setSelectedSuburb] = useState(null);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= MOBILE_BREAKPOINT);
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return sessionStorage.getItem('marketActivityWelcomeSeen') !== 'true'; }
    catch (_) { return true; }
  });
  // Ref to the search pill so the portaled dropdown can match its width/left edge.
  const pillRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (agentDetails) {
      const title = `Market Activity by ${agentDetails.agency_name_short}`;
      const description = `Explore recent sold and rented properties in any suburb. Compare prices and days on market with ${agentDetails.agency_name_short}.`;
      setMetaTags({
        title,
        description,
        keywords: 'Market Activity, Sold Properties, Rented Properties, Comparable Sales',
        url: 'https://homeprice.site/market-activity',
        image: agentDetails.landing_background_url,
      });
    }
  }, [agentDetails]);

  // Search suburbs via the shared location-search endpoint. The Market Activity
  // component is suburb-based, so we only surface suburb results (not postcodes).
  const loadSuburbs = debounce((inputValue, callback) => {
    if (!inputValue || inputValue.length <= 1) {
      callback([]);
      return;
    }
    axios.get(`${window.api_host}/location-search?q=${inputValue}`)
      .then((response) => {
        const options = response.data
          .filter((r) => r.type === 'suburb')
          .map((r) => ({
            value: r.value,
            label: r.state ? `${r.label}, ${r.state}` : r.label,
          }));
        callback(options);
      })
      .catch((error) => {
        console.error('Error loading suburb options:', error);
        callback([]);
      });
  }, 500);

  const handleSuburbChange = (option) => {
    setSelectedSuburb(option);
    if (option) {
      const slug = agentDetails?.landing_page_slug;
      trackEvent('select_suburb', {
        source: 'market_activity',
        suburb: option.label,
        suburb_id: option.value,
        agent_slug: slug,
      });
      // Notify the agent that the visitor is actively viewing market activity
      // for a specific suburb. Fires only once a suburb is selected so the
      // notification always carries a meaningful suburb value.
      handleTokenAndPostRequest(
        `${window.api_host}/save-settings`,
        {
          agent_slug: slug,
          event_type: 'market_activity_view',
          source: window.location.href,
          metadata: { suburb: option.label },
        },
        { 'Content-Type': 'application/json' }
      ).catch((err) => console.error('Error sending market_activity_view alert:', err));
    }
  };

  // "Got it" on the welcome modal: persist dismissal and track the click. No
  // agent notification is sent here — the Market Activity Viewed notification
  // fires once the visitor actually selects a suburb (see handleSuburbChange).
  const handleWelcomeDismiss = () => {
    setShowWelcome(false);
    try { sessionStorage.setItem('marketActivityWelcomeSeen', 'true'); } catch (_) { /* ignore */ }
    trackEvent('market_activity_welcome_dismissed', { agent_slug: agentDetails?.landing_page_slug });
  };

  const accent = branding.accentColor;

  const selectStyles = {
    control: (p) => ({
      ...p, border: 'none', boxShadow: 'none', backgroundColor: 'transparent',
      minHeight: isMobile ? 50 : 46, cursor: 'text',
    }),
    valueContainer: (p) => ({ ...p, padding: '2px 4px' }),
    placeholder: (p) => ({ ...p, color: '#94a3b8', textAlign: 'left', fontSize: isMobile ? 16 : '1rem' }),
    input: (p) => ({ ...p, textAlign: 'left', color: '#0f172a', fontSize: isMobile ? 16 : undefined }),
    singleValue: (p) => ({ ...p, textAlign: 'left', fontWeight: 600, color: '#0f172a', fontSize: isMobile ? 16 : undefined }),
    menu: (p) => ({
      ...p, textAlign: 'left', borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 12px 32px rgba(15,23,42,0.16)', marginTop: 8,
    }),
    menuPortal: (p) => {
      const rect = pillRef.current?.getBoundingClientRect();
      return { ...p, zIndex: 99999, ...(rect ? { left: rect.left, width: rect.width } : {}) };
    },
    option: (p, state) => ({
      ...p,
      backgroundColor: state.isFocused ? `${accent}12` : '#fff',
      color: '#0f172a', fontSize: '0.95rem', cursor: 'pointer', padding: '12px 14px',
      '&:active': { backgroundColor: `${accent}22` },
    }),
    dropdownIndicator: () => ({ display: 'none' }),
    indicatorSeparator: () => ({ display: 'none' }),
    loadingIndicator: (p) => ({ ...p, color: accent }),
  };

  // ── Skeleton (initial agent/branding load) ──
  if (!agentDetails) {
    return (
      <div
        className={`psl-wrap ${isMobile ? 'psl-wrap--mobile' : 'psl-wrap--desktop'} ma-hero--compact`}
        style={{ '--psl-accent': accent }}
      >
        <div className={`psl-inner ${isMobile ? 'psl-inner--mobile' : 'psl-inner--desktop'}`} style={{ width: '100%' }}>
          <Skeleton width={isMobile ? 220 : 320} height={isMobile ? 34 : 48} style={{ marginBottom: 14 }} />
          <Skeleton width={isMobile ? 280 : 440} height={16} style={{ marginBottom: 28 }} />
          <Skeleton height={isMobile ? 62 : 60} borderRadius={16} style={{ width: '100%' }} />
        </div>
      </div>
    );
  }

  const heroCompact = Boolean(selectedSuburb);
  // Branded landing (an actual agent has been resolved) — show their profile +
  // contact button, similar to /property-search-live.
  const isBranded =
    agentDetails &&
    agentDetails.landing_page_slug !== 'home-price' &&
    agentDetails.agent_name;
  const trustPoints = [
    { icon: <FaChartLine size={isMobile ? 13 : 14} />, label: 'Recent sales & rentals' },
    { icon: <FaMapMarkerAlt size={isMobile ? 13 : 14} />, label: 'Local suburb data' },
    { icon: <FaRegClock size={isMobile ? 13 : 14} />, label: 'Updated regularly' },
  ];

  // A bordered suburb search for the comparables filter modal (mobile). Bound to
  // the same state as the hero search, so changing it here reloads the results.
  const modalSuburbSearch = (
    <AsyncSelect
      cacheOptions
      loadOptions={loadSuburbs}
      onChange={handleSuburbChange}
      value={selectedSuburb}
      placeholder="Search for a suburb…"
      noOptionsMessage={({ inputValue }) => (inputValue && inputValue.length > 1 ? 'No suburbs found' : null)}
      isClearable
      isSearchable
      menuPortalTarget={document.body}
      menuPlacement="auto"
      maxMenuHeight={240}
      aria-label="Suburb"
      styles={{
        control: (p) => ({ ...p, borderRadius: 10, borderColor: '#e5e7eb', minHeight: 46 }),
        placeholder: (p) => ({ ...p, color: '#94a3b8', textAlign: 'left' }),
        input: (p) => ({ ...p, textAlign: 'left', color: '#0f172a' }),
        singleValue: (p) => ({ ...p, textAlign: 'left', fontWeight: 600, color: '#0f172a' }),
        menu: (p) => ({ ...p, textAlign: 'left', zIndex: 9998 }),
        // Above the mobile filters Modal (z-index 100000) so the menu is clickable inside it.
        menuPortal: (p) => ({ ...p, zIndex: 100001 }),
        menuList: (p) => ({
          ...p,
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 transparent',
          '::-webkit-scrollbar': { width: 8 },
          '::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 8 },
          '::-webkit-scrollbar-thumb:hover': { backgroundColor: '#94a3b8' },
          '::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }),
        option: (p, state) => ({ ...p, backgroundColor: state.isFocused ? `${accent}14` : '#fff', color: '#1a1a1a', textAlign: 'left', cursor: 'pointer' }),
      }}
    />
  );

  return (
    <div
      className={`ma-page ${heroCompact ? 'ma-page--results' : 'ma-page--landing'}`}
      style={{
        '--psl-accent': accent,
        '--psl-accent-ring': `${accent}26`,
        '--psl-accent-soft': `${accent}14`,
      }}
    >
      <div
        className={[
          'psl-wrap',
          isMobile ? 'psl-wrap--mobile' : 'psl-wrap--desktop',
          heroCompact ? 'ma-hero--compact' : '',
        ].join(' ')}
      >
        <div className="psl-blob psl-blob--a" style={{ backgroundColor: accent }} aria-hidden="true" />
        <div className="psl-blob psl-blob--b" style={{ backgroundColor: accent }} aria-hidden="true" />

        <div className={`psl-inner ${isMobile ? 'psl-inner--mobile' : 'psl-inner--desktop'}`}>
          <h1 className={`psl-title ${isMobile ? 'psl-title--mobile' : 'psl-title--desktop'}`}>
            {heroCompact ? (
              <>Market <span style={{ color: accent }}>Activity</span></>
            ) : (
              <>What are <span style={{ color: accent }}>similar properties</span> selling for?</>
            )}
          </h1>
          <p className={`psl-sub ${isMobile ? 'psl-sub--mobile' : 'psl-sub--desktop'}`}>
            Search for a suburb to see recent sold and rented properties and how they compare.
          </p>

          <div className="psl-searchcard">
            <div className={`psl-search-pill ${isMobile ? 'psl-search-pill--mobile' : ''}`} ref={pillRef}>
              <span className="psl-search-icon"><FaMapMarkerAlt size={isMobile ? 17 : 18} /></span>
              <div className="psl-search-select">
                <AsyncSelect
                  cacheOptions
                  loadOptions={loadSuburbs}
                  onChange={handleSuburbChange}
                  value={selectedSuburb}
                  placeholder="Search for a suburb…"
                  noOptionsMessage={({ inputValue }) => (inputValue && inputValue.length > 1 ? 'No suburbs found' : null)}
                  isClearable
                  isSearchable
                  backspaceRemovesValue
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  aria-label="Suburb"
                />
              </div>
            </div>
          </div>

          {!heroCompact && (
            <div className={`psl-trust ${isMobile ? 'psl-trust--mobile' : ''}`}>
              {trustPoints.map((t, i) => (
                <span className="psl-trust-item" key={i}>{t.icon}{t.label}</span>
              ))}
            </div>
          )}

          {!heroCompact && isBranded && (
            <div className={`psl-agent ${isMobile ? 'psl-agent--mobile' : ''}`}>
              {agentDetails.agent_profile_image && (
                <img
                  className="psl-agent-photo"
                  src={agentDetails.agent_profile_image}
                  alt={agentDetails.agent_name}
                />
              )}
              <div className="psl-agent-info">
                <div className="psl-agent-name">{agentDetails.agent_name}</div>
                <div className="psl-agent-agency">{agentDetails.agency_name_short}</div>
              </div>
              <button
                type="button"
                className="psl-agent-btn"
                onClick={() => setShowEnquiry(true)}
                style={{ color: accent }}
              >
                Contact {agentDetails.agent_name?.split(' ')[0] || 'us'}
              </button>
            </div>
          )}
        </div>
      </div>

      {selectedSuburb ? (
        <Container className="ma-results" style={{ maxWidth: 1140 }}>
          {isBranded && (
            <div className={`ma-agent-strip ${isMobile ? 'ma-agent-strip--mobile' : ''}`}>
              {agentDetails.agent_profile_image && (
                <img
                  className="ma-agent-strip-photo"
                  src={agentDetails.agent_profile_image}
                  alt={agentDetails.agent_name}
                />
              )}
              <div className="ma-agent-strip-info">
                <div className="ma-agent-strip-name">{agentDetails.agent_name}</div>
                <div className="ma-agent-strip-agency">{agentDetails.agency_name_short}</div>
              </div>
              <button
                type="button"
                className="ma-agent-strip-btn"
                onClick={() => setShowEnquiry(true)}
                style={{ backgroundColor: accent, color: branding.buttonTextColor, borderColor: branding.isLight ? '#222' : accent }}
              >
                Contact {agentDetails.agent_name?.split(' ')[0] || 'us'}
              </button>
            </div>
          )}
          <ComparableProperties
            key={selectedSuburb.value}
            suburb={selectedSuburb.value}
            agentDetails={agentDetails}
            suburbSearch={modalSuburbSearch}
            subtitle="Find out how your property compares to others in the area."
            modalContent={(
              <ol>
                <li>Find a list of sold or rented properties using the filters provided. Try to find at least 5 if you can.</li>
                <li>Exclude properties which are too different from your property.</li>
                <li>The median price, max price, and min price of your final list is then calculated.</li>
                <li>This would be an estimate of the price range for your property.</li>
              </ol>
            )}
          />
        </Container>
      ) : null}

      <EnquiryModal
        show={showEnquiry}
        onHide={() => setShowEnquiry(false)}
        agentDetails={agentDetails}
      />

      {/* Welcome modal — full screen on mobile. "Got it" notifies the agent. */}
      <Modal
        show={showWelcome}
        onHide={handleWelcomeDismiss}
        centered={!isMobile}
        fullscreen={isMobile ? true : undefined}
        className="ma-welcome-modal"
        style={{ zIndex: 9999 }}
      >
        <Modal.Body
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: isMobile ? '2rem 1.5rem' : '2.5rem 3rem 2rem',
            background: 'linear-gradient(180deg, #fafafa 0%, #fff 100%)',
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
            <div style={{ width: 48, height: 48, borderRadius: '50%', background: `${accent}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
              <FaChartLine style={{ color: accent, fontSize: '1.2rem' }} />
            </div>
            <h5 style={{ fontWeight: 800, fontSize: isMobile ? '1.3rem' : '1.2rem', color: '#111827', marginBottom: 8, letterSpacing: '-0.02em' }}>
              Welcome to Market Activity
            </h5>
            <p style={{ fontSize: '0.92rem', color: '#56607a', lineHeight: 1.55, margin: '0 auto', maxWidth: 420 }}>
              See what&rsquo;s really happening in any suburb. Explore recent sold and rented
              properties, compare prices and days on market, and build your own price range
              from real comparable sales.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: '0.5rem', maxWidth: 420, marginLeft: 'auto', marginRight: 'auto', width: '100%' }}>
            {WELCOME_STEPS.map((step) => (
              <div key={step.icon} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', backgroundColor: `${accent}14`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <span style={{ fontSize: '0.72rem', fontWeight: 700, color: accent }}>{step.icon}</span>
                </div>
                <span style={{ fontSize: '0.88rem', color: '#374151', lineHeight: 1.5 }}>{step.text}</span>
              </div>
            ))}
          </div>
        </Modal.Body>
        <Modal.Footer style={{ borderTop: 'none', padding: isMobile ? '0 1.5rem 2rem' : '0 3rem 1.75rem', justifyContent: 'center' }}>
          <Button
            onClick={handleWelcomeDismiss}
            style={{
              backgroundColor: accent,
              borderColor: branding.isLight ? '#222' : accent,
              color: branding.buttonTextColor,
              borderRadius: 12,
              fontWeight: 600,
              fontSize: '0.95rem',
              padding: '12px 32px',
              width: '100%',
              maxWidth: 420,
            }}
          >
            Got it
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MarketActivity;
